// src/utils/plan/planMatch.js
//
// Relates a session's subject / session type to a plan (SAT, ACT, or custom).
// Kept separate from planConfig so plan-shape concerns and session-matching
// concerns stay independent.

import { planSubjectOrder, planTypeOf, isCustomPlan } from "./planConfig";
import { sameSubject, bareTestName, testNameIn, subjectMatchKey } from "../subjectMatch";

/**
 * Map a session subject/type string to one of the plan's subjects
 * (null = no specific focus). Understands the canonical SAT
 * (Reading & Writing / Math) and ACT (English / Math / Reading / Science)
 * sections, and falls back to matching against the plan's own subject names so
 * custom plans with arbitrary subjects work too.
 */
export function subjectFromSessionType(t, subjects = []) {
  const list = Array.isArray(subjects) ? subjects : [];
  const pick = (name) => (list.includes(name) ? name : null);
  const s = String(t || "").toLowerCase();
  if (!s) return null;

  // Canonical SAT/ACT sections first (handles "SAT Math", "ACT English", ...).
  const canonical =
    (s.includes("math") && "Math") ||
    (s.includes("science") && "Science") ||
    (s.includes("english") && "English") ||
    (s.includes("reading") && "Reading") ||
    ((s.includes("writing") || s.includes("r/w") || s.includes("rw") || s.includes("verbal")) &&
      "Reading & Writing") ||
    null;
  if (canonical) {
    const hit = pick(canonical);
    if (hit) return hit;
  }

  // Custom plans: match the session subject against the plan's own subjects.
  return list.find((name) => sameSubject(name, t)) || null;
}

/**
 * True when a plan covers the session's subject/type. An unknown/blank session
 * subject is treated as a match so useful context isn't hidden before the tutor
 * has picked a session type.
 */
export function planMatchesSessionSubject(plan, sessionSubject) {
  if (!String(sessionSubject || "").trim()) return true;

  // A bare test name ("SAT", "PSAT", "ACT") means "no specific subject" — match
  // any plan for that test so a generic session still shows the right plan.
  const test = bareTestName(sessionSubject);
  if (test) return planTypeOf(plan) === test;

  // A named test ("SAT Math", "ACT English") must not surface another test's
  // plan, even when the subject name is shared. Custom plans declare their own
  // subjects and are never filtered by test name.
  const named = testNameIn(sessionSubject);
  if (named && !isCustomPlan(plan) && planTypeOf(plan) !== named) return false;

  const subjects = planSubjectOrder(plan);
  if (!subjects.length) return false;
  if (subjectFromSessionType(sessionSubject, subjects)) return true;
  return subjects.some((s) => sameSubject(s, sessionSubject));
}

/** Normalised, non-empty subject keys a plan actually covers. */
export function planSubjectKeys(plan) {
  return new Set(
    planSubjectOrder(plan)
      .map((s) => subjectMatchKey(s))
      .filter(Boolean)
  );
}

/**
 * True when two plans cover at least one of the same subjects — i.e. they
 * compete for the same ground and cannot both be active. Two plans with
 * disjoint subjects (an SAT Math plan and a Reading & Writing plan, or a custom
 * Chemistry plan beside an SAT plan) may both be active at once, as may an SAT
 * and an ACT plan that share a subject name (see below).
 *
 * A plan that declares no subjects at all can't be disambiguated, so it is
 * treated as conflicting (conservative: don't silently run two at once).
 */
export function plansConflict(a, b) {
  const ka = planSubjectKeys(a);
  const kb = planSubjectKeys(b);
  if (!ka.size || !kb.size) return true;
  let shared = false;
  for (const k of ka) {
    if (kb.has(k)) {
      shared = true;
      break;
    }
  }
  if (!shared) return false;
  // Different standard tests never compete, even when a subject name is
  // shared: "SAT Math" and "ACT Math" are separate plans serving separate
  // sessions. Custom plans have no test, so they do conflict on a shared
  // subject (avoids two cards for the same ground).
  if (!isCustomPlan(a) && !isCustomPlan(b) && planTypeOf(a) !== planTypeOf(b)) return false;
  return true;
}

/**
 * Drop non-active plans that overlap an active one, so a draft/duplicate copy of
 * an active plan doesn't render next to it. Plans for subjects with no active
 * counterpart are kept, so un-activated plans stay visible.
 */
export function preferActivePlans(plans) {
  const list = plans || [];
  const active = list.filter((p) => p?.status === "active");
  if (!active.length) return list;
  return list.filter((p) => p?.status === "active" || !active.some((a) => plansConflict(a, p)));
}
